import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

const SimpleHorizontalScroll: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxScroll, setMaxScroll] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const scrollStep = 350;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateMaxScroll = () => {
      if (el.scrollWidth - el.clientWidth > 0) {
        setMaxScroll(el.scrollWidth - el.clientWidth);
      } else {
        setMaxScroll(el.clientWidth);
      }
    };

    updateMaxScroll();
    window.addEventListener("resize", updateMaxScroll);
    return () => window.removeEventListener("resize", updateMaxScroll);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollOffset(el.scrollLeft);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollLeft = () => {
    containerRef.current?.scrollBy({ left: -scrollStep, behavior: "smooth" });
  };

  const scrollRight = () => {
    containerRef.current?.scrollBy({ left: scrollStep, behavior: "smooth" });
  };

  return (
    <div className="relative flex flex-col space-y-2">
      <div
        id="budget-carousel"
        ref={containerRef}
        className="flex flex-nowrap space-x-4 overflow-x-auto"
      >
        {children}
      </div>

      <div
        className={`pointer-events-none absolute left-0 top-0 -ml-1 h-full w-8
                        bg-gradient-to-r from-white to-transparent transition-all ${
                          scrollOffset > 0 ? "opacity-100" : "opacity-0"
                        }`}
      />

      <div
        className={`pointer-events-none absolute right-0 top-0 h-full w-8
                        bg-gradient-to-l from-white to-transparent ${
                          scrollOffset < maxScroll ? "opacity-100" : "opacity-0"
                        }`}
      />

      {/* arrow buttons */}
      <div className="flex flex-row space-x-2 self-end">
        <button
          disabled={scrollOffset <= 0}
          onClick={scrollLeft}
          className={`relative cursor-pointer items-center rounded-lg border px-2 ${
            scrollOffset > 0
              ? "hover:bg-light-background active:bg-light-background"
              : ""
          }`}
        >
          <ChevronLeftIcon
            className={`h-10 w-6 transition-colors duration-300 ease-out ${
              scrollOffset <= 0 ? "text-gray-300" : "text-primary2"
            }`}
          />
        </button>
        <button
          disabled={scrollOffset >= maxScroll}
          onClick={scrollRight}
          className={`relative cursor-pointer items-center rounded-lg border px-2 ${
            scrollOffset < maxScroll
              ? "hover:bg-light-background active:bg-light-background"
              : ""
          }`}
        >
          <ChevronRightIcon
            className={`h-10 w-6 transition-colors duration-300 ease-out ${
              scrollOffset >= maxScroll ? "text-gray-300" : "text-primary2"
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default SimpleHorizontalScroll;
